import React from 'react';
import {Box, Typography, Button, Stack, Divider, CircularProgress} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTaskDetailsById, useAddTaskDetails, useDeleteTaskDocumentsById, useCompleteOnboarding } from '../../../hooks/useClientOnboarding';
import backarrow from '../../../assets/images/backarrow.svg';
import { ContainerHeader } from '../../app-table/components/StyledComponents';
import SLAProgress from './components/SLAProgress';
import StatusStepper from './components/StatusStepper';
import { AppChip, AppButton, AppOutlineButton } from '../../StyledComponent';
import { UploadDropzone, UploadIconStyled } from '../../../assets/css/styelComponent';
import FileCard from '../../AppComponents/FileCard';
import AppRichTextEditor from '../../AppComponents/AppRichTextEditor';
import { handleDownloadFile } from '../../../utils/helpers';
import UnsavedChangesDialog from '../../dialog-box/UnsavedChangesDialog';
import ConfirmDeleteDialogBox from '../../dialog-box/ConfirmDeleteDialogBox';
import OnboardingDialogBox from '../../dialog-box/OnboardingDialogBox';
import { commonApi } from '../../../components/api/common';
import {TaskProgressSidebarContainer, TaskProgressTitle, TaskListContainer, 
  TaskItemWrapper, 
  TaskConnectorLine, 
  TaskItemContent, 
  StatusStepperContainer, 
  TaskTitle,
  TaskDetailsMainContainer,
  TaskDetailsContentWrapper,
  TaskDetailsMainContent,
  TaskDetailsFooter
} from './components/styledComponents';

// Task status configuration
const getStatusConfig = (status) => {
  const statusMap = {
    completed: {
      label: 'Completed',
      backgroundColor: '#39cb7f26',
      textColor: '#39cb7f'
    },
    inprogress: {
      label: 'In Progress',
      backgroundColor: '#fdd43f26',
      textColor: '#fdd43f'
    },
    pending: {
      label: 'To Do',
      backgroundColor: 'F5F5F5',
      textColor: '616161'
    }
  };
  
  const normalizedStatus = status?.toLowerCase();
  return statusMap[normalizedStatus] || statusMap.pending;
};

// Header Component
const Header = ({ productName, familyName, sla, remainingDays, onBack, onComplete, isCompleteEnabled, overallStatus, isCompleted }) => (
  <ContainerHeader sx={theme => ({ p: 2, height: 'auto', minHeight: theme.mixins.toolbar.minHeight })}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '33%' }}>
        <Button onClick={onBack} variant="text" sx={{ minWidth: 'auto', p: 0 }}>
          <img src={backarrow} alt="Back" style={{ width: 24, height: 24 }} />
        </Button>
        <Stack spacing={1}>
          <Typography variant="caption" sx={theme => ({ color: theme.palette.text.secondary, fontWeight: 500 })}>
            {productName}
          </Typography>
          <Typography variant="h5" component="h1" sx={theme => ({ color: theme.palette.custom.brown.main, fontWeight: 500 })}>
            {familyName}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ width: '33%', display: 'flex', justifyContent: 'center' }}>
        <SLAProgress sla={sla} remainingDays={remainingDays} />
      </Box>
      <Box sx={{ width: '33%', display: 'flex', justifyContent: 'flex-end' }}>
        <AppButton
          onClick={onComplete}
          disabled={!isCompleteEnabled || overallStatus === "Completed" || isCompleted}
          variant="contained"
          size="small"
          sx={{ fontSize: 15 }}
        >
          Complete
        </AppButton>
      </Box>
    </Box>
  </ContainerHeader>
);

const TaskProgressSidebar = React.memo(({ tasks, selectedTaskId, onTaskClick }) => (
  <TaskProgressSidebarContainer>
    <TaskProgressTitle variant="subtitle1">
      Task Progress
    </TaskProgressTitle>
    <TaskListContainer>
      {tasks.map((task, index) => (
        <TaskItemWrapper key={task.id}>
          {index < tasks.length - 1 && <TaskConnectorLine />}
          <TaskItemContent onClick={() => onTaskClick(task.id)}>
            <StatusStepperContainer>
              <StatusStepper status={task.status} />
            </StatusStepperContainer>
            <TaskTitle 
              variant="body2" 
              status={task.status}
              isSelected={selectedTaskId === task.id}
            >
              {task.title}
            </TaskTitle>
          </TaskItemContent>
        </TaskItemWrapper>
      ))}
    </TaskListContainer>
  </TaskProgressSidebarContainer>
));

const ContentSection = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle1" sx={theme => ({ color: theme.palette.text.primary, fontWeight: 500, mb: 1 })}>
      {title}
    </Typography>
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 0 }}>
      {children}
    </Box>
  </Box>
);

const TaskDetails = () => {
  const navigate = useNavigate();
  const { productClientId } = useParams();
  const [documents, setDocuments] = React.useState([]);
  const [comments, setComments] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [isCompleting, setIsCompleting] = React.useState(false);
  const [selectedTaskId, setSelectedTaskId] = React.useState(null);
  const [taskData, setTaskData] = React.useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [documentToDelete, setDocumentToDelete] = React.useState(null);  
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = React.useState(false);
  const [isTaskCompletionDialogOpen, setIsTaskCompletionDialogOpen] = React.useState(false);
  const { data: taskDetailsData, isLoading, error, refetch } = useGetTaskDetailsById(productClientId);
  const { mutateAsync: addTaskDetails, isLoading: isUploading } = useAddTaskDetails();
  const { mutateAsync: deleteTaskDocument, isLoading: isDeletingDocument } = useDeleteTaskDocumentsById();
  const { mutateAsync: completeOnboarding, isLoading: isCompletingOnboarding } = useCompleteOnboarding();
  const tasks = React.useMemo(() => {
    if (!taskDetailsData?.data?.tasks) return [];
    
    return taskDetailsData.data.tasks
      .sort((a, b) => a.orderNo - b.orderNo) 
      .map((task) => ({
        id: task.taskId,
        title: task.taskName,
        status: task.taskStatus?.toLowerCase() || 'pending',
        isMandatory: task.mandatory,
        orderNo: task.orderNo,
        comments: task.comments,
        clientDocuments: task.clientDocuments || [],
        taskDocuments: task.taskDocuments || []
      }));
  }, [taskDetailsData]);

  const saveCurrentTaskData = React.useCallback(() => {
    if (selectedTaskId) {
      setTaskData(prev => ({
        ...prev,
        [selectedTaskId]: {
          documents: [...documents],
          comments: comments
        }
      }));
    }
  }, [selectedTaskId, documents, comments]);

  const loadTaskData = React.useCallback((taskId) => {
    const savedData = taskData[taskId];
    if (savedData) {
      setDocuments(savedData.documents || []);
      setComments(savedData.comments || '');
    } else {
      const task = tasks.find(t => t.id === taskId);
      setDocuments([]);
      setComments(task?.comments || '');
    }
  }, [taskData, tasks]);

  React.useEffect(() => {
    if (tasks.length > 0 && !selectedTaskId) {
      const inProgressTask = tasks.find(task => task.status === 'inprogress');
      const initialTaskId = inProgressTask?.id || tasks[0]?.id;
      setSelectedTaskId(initialTaskId);
      loadTaskData(initialTaskId);
    }
  }, [tasks, selectedTaskId, loadTaskData]);

  const selectedTask = tasks.find(task => task.id === selectedTaskId) || tasks[0];
  
  // Check if both overall status and task status are completed
  const isCompleted = React.useMemo(() => {
    const overallStatus = taskDetailsData?.data?.overallStatus;
    const taskStatus = selectedTask?.status;
    return overallStatus === 'Completed' && taskStatus === 'completed';
  }, [taskDetailsData?.data?.overallStatus, selectedTask?.status]);
  
  // Check if all mandatory tasks are completed
  const areAllMandatoryTasksCompleted = React.useMemo(() => {
    const mandatoryTasks = tasks.filter(task => task.isMandatory);
    if (mandatoryTasks.length === 0) {
      return true;
    }
    return mandatoryTasks.every(task => task.status === 'completed');
  }, [tasks]);
  
  // Check if there are any incomplete tasks
  const hasIncompleteTasks = React.useMemo(() => {
    return tasks.some(task => task.status !== 'completed');
  }, [tasks]);
  
  // Check if current task has unsaved changes
  const checkUnsavedChanges = React.useCallback(() => {
    if (!selectedTaskId) return false;
    
    const currentTaskFromServer = tasks.find(t => t.id === selectedTaskId);
    const serverComments = currentTaskFromServer?.comments || '';
    
    return (
      documents.length > 0 || 
      comments !== serverComments
    );
  }, [selectedTaskId, documents, comments, tasks]);

  // Track unsaved changes
  React.useEffect(() => {
    setHasUnsavedChanges(checkUnsavedChanges());
  }, [checkUnsavedChanges]);

  const handleTaskClick = React.useCallback((taskId) => {
    if (taskId !== selectedTaskId) {
      saveCurrentTaskData(); 
      setSelectedTaskId(taskId);
      loadTaskData(taskId); 
    }
  }, [selectedTaskId, saveCurrentTaskData, loadTaskData]);

  const handleFileUpload = (e) => setDocuments([...documents, ...Array.from(e.target.files)]);
  const handleDeleteFile = (index) => setDocuments(documents.filter((_, i) => i !== index));

  const handleTaskDocumentDownload = async (document) => {
    console.log("document", document);
    try {
      if(!document.url) return;
      const signedUrlResponse = await commonApi.getSignedUrl({
        key: document.url,
        fileName: document.name
      });
      
      if (signedUrlResponse?.data?.signedUrl) {
        handleDownloadFile({
          name: document.name,
          url: signedUrlResponse.data.signedUrl
        });
      } else {
        console.error('No signed URL received');
      }
    } catch (error) {
      console.error('Error getting signed URL:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedTask) {
      return;
    }
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('productClientTaskId', selectedTask.id);
      formData.append('comments', comments || '');
      formData.append('status', true);
      documents.forEach((file, index) => {
        formData.append('document', file);
      });      
      await addTaskDetails(formData);   
      setDocuments([]);
      setTaskData(prev => {
        const updated = { ...prev };
        delete updated[selectedTask.id];
        return updated;
      });
      await refetch();
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedDialog(false);
    navigate(-1);
  };

  const handleCancelLeave = () => {
    setShowUnsavedDialog(false);
  };

  const handleDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentToDelete || !productClientId) {
      console.error('Missing required data for document deletion');
      return;
    }
    try {
      const payload = {
        documentId: documentToDelete.id,
      };
      await deleteTaskDocument(payload);
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
      await refetch();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleCompleteClick = () => {
    setIsCompletionDialogOpen(true);
  };

  const handleCloseCompletionDialog = () => {
    setIsCompletionDialogOpen(false);
  };

  const handleConfirmCompletion = async () => {
    setIsCompletionDialogOpen(false);
    await completeOnboarding({
      productClientId: productClientId,
      status: "completed"
    });
    navigate(-1);
  };

  const handleCompleteTask = () => {
    setIsTaskCompletionDialogOpen(true);
  };

  const handleCloseTaskCompletionDialog = () => {
    setIsTaskCompletionDialogOpen(false);
  };

  const handleConfirmTaskCompletion = async () => {
    if (!selectedTask) {
      return;
    }

    setIsCompleting(true);
    try {
      const formData = new FormData();
      formData.append('productClientTaskId', selectedTask.id);
      formData.append('comments', comments || '');
      formData.append('status', false);
      documents.forEach((file, index) => {
        formData.append('document', file);
      });
      await addTaskDetails(formData);
      setDocuments([]);
      setTaskData(prev => { 
        const updated = { ...prev };
        delete updated[selectedTask.id];
        return updated;
      });
      await refetch();
      setIsTaskCompletionDialogOpen(false);
      
    } catch (error) {
      console.error('Error completing task:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center">
          <CircularProgress size={20} />
      </Box>
    );
  }

  const getDynamicKeyFields = (document) => {
    // Create a new object with all keys from the input object,
    // but for each key, if there are similar keys like doumentName, docName, doumentNam, etc.,
    // use the value from whichever exists in the object.
    // We'll try to find the value for each key by checking for common misspellings.
    const keyVariants = {
      id: ['id', 'documentId', 'docId', 'documentI' ],
      name: ['documentName', 'docName', 'documentNam' ],
      url: ['documentPath', 'docPath', 'documentPat' ],
      size: ['documentSize', 'docSize', 'documentSiz' ],
      type: ['documentType', 'docType', 'documentTyp' ],
    };

    const result = {};

    Object.keys(keyVariants).forEach((key) => {
      for (let variant of keyVariants[key]) {
        if (document?.[variant] !== undefined) {
          result[key] = document[variant];
          break;
        }
      }
    });
    
    // Add all other keys as-is
    Object.keys(document).forEach((key) => {
      if (!Object.values(keyVariants).flat().includes(key)) {
        result[key] = document[key];
      }
    });
    return result;
  };
  return (
    <TaskDetailsMainContainer>
      <Header
        productName={taskDetailsData?.data?.product?.productName || "Loading..."}
        familyName={`${taskDetailsData?.data?.customerName || ""} - ${taskDetailsData?.data?.clientName || ""}`}
        sla={taskDetailsData?.data?.product?.slaCount || 0}
        remainingDays={taskDetailsData?.data?.remainingDays || 0}
        onBack={handleBackClick}
        onComplete={handleCompleteClick}
        isCompleteEnabled={areAllMandatoryTasksCompleted}
        overallStatus={taskDetailsData?.data?.overallStatus}
        isCompleted={isCompleted}
      />
      <Divider />
      <TaskDetailsContentWrapper>
        <TaskProgressSidebar 
          tasks={tasks} 
          selectedTaskId={selectedTaskId}
          onTaskClick={handleTaskClick}
        />
        <TaskDetailsMainContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="caption" sx={theme => ({ color: theme.palette.text.secondary, display: 'block', mb: 0.5 })}>
                Task Name
              </Typography>
              <Typography variant="h3" sx={theme => ({ color: theme.palette.text.primary, fontWeight: 500 })}>
                {selectedTask?.title || "No task selected"}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedTask?.isMandatory && (
                <AppChip 
                  label="Mandatory" 
                  backgroundColor="#fc4b6c26" 
                  textColor="#fc4b6c" 
                  fontSize="0.75rem" 
                />
              )}
              <AppChip {...getStatusConfig(selectedTask?.status || 'pending')} fontSize="0.875rem" />
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <ContentSection title="Task Related Documents">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              
            {(!selectedTask?.taskDocuments || !selectedTask.taskDocuments?.length) ? (
                <Typography variant="body2" color="text.secondary">
                  No task documents available
                </Typography>
              ) : (
              selectedTask?.taskDocuments?.map((doc, index) => (
                <FileCard
                  key={index}
                  file={getDynamicKeyFields(doc)}
                  onDownload={() => { 
                   handleTaskDocumentDownload(getDynamicKeyFields(doc));
                  }}
                  showDelete={false}
                />
              ))
              )}
            </Box>
          </ContentSection>
          <Divider sx={{ mb: 2 }} />

          <ContentSection title="Upload Documents">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={isCompleted ? { 
                opacity: 0.6, 
                pointerEvents: 'none',
                cursor: 'not-allowed'
              } : {}}>
                <UploadDropzone as="label">
                  <input 
                    hidden 
                    type="file" 
                    multiple 
                    onChange={handleFileUpload}
                    disabled={isCompleted}
                  />
                  <UploadIconStyled />
                  <Typography sx={{ fontSize: 11, color: theme => theme.palette.text.primary, textAlign: "center" }}>
                    Upload
                  </Typography>
                </UploadDropzone>
              </Box>
              
              {/* Show existing uploaded documents from server */}
              {selectedTask?.clientDocuments?.map((doc, index) => (
                <FileCard
                  key={`server-${index}`}
                  file={{
                    id: doc.id,
                    name: doc.documentName,
                    size: doc.documentSize,
                    url: doc.documentPath
                  }}
                  onDownload={() => {
                    if (doc.documentPath) {
                      handleTaskDocumentDownload({
                        name: doc.documentName,
                        url: doc.documentPath
                      });
                    }
                  }}
                  onDelete={!isCompleted ? () => handleDeleteDocument(doc) : undefined}
                  showDelete={!isCompleted}
                />
              ))}
              
              {/* Show newly uploaded documents */}
              {documents.map((file, idx) => (
                <FileCard
                  key={`new-${idx}`}
                  file={file}
                  onDelete={!isCompleted ? () => handleDeleteFile(idx) : undefined}
                  onDownload={() => handleDownloadFile(file)}
                  showDelete={!isCompleted}
                />
              ))}
            </Box>
          </ContentSection>
          <Divider sx={{ mb: 3 }} />

          <ContentSection title="Comments">
            <AppRichTextEditor
              value={comments}
              onChange={setComments}
              placeholder="Add your comments here..."
              minHeight="150px"
              disabled={isCompleted}
            />
          </ContentSection>
        </TaskDetailsMainContent>
      </TaskDetailsContentWrapper>

      <TaskDetailsFooter>
        <AppOutlineButton 
          onClick={handleSave} 
          disabled={isSaving || isUploading || !selectedTask || selectedTask?.status === 'completed' || isCompleted} 
          size="small" 
          sx={{ fontSize: 15 }}
        >
          {isSaving || isUploading ? 'Saving...' : 'Save'}
        </AppOutlineButton>
        <AppButton 
          onClick={handleCompleteTask} 
          disabled={isCompleting || isUploading || !selectedTask || selectedTask?.status === 'completed' || isCompleted} 
          variant="contained" 
          size="small"
        >
          {isCompleting ? 'Completing...' : 'Complete Task'}
        </AppButton>
      </TaskDetailsFooter>
      
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
      <ConfirmDeleteDialogBox
        isConfirmOpen={isDeleteDialogOpen}
        handleConfirmClose={handleCloseDeleteDialog}
        handleDeleteHoliday={handleConfirmDeleteDocument}
        isLoading={isDeletingDocument}
      />
      
      <OnboardingDialogBox
        isConfirmOpen={isCompletionDialogOpen}
        handleConfirmClose={handleCloseCompletionDialog}
        handleConfirmOnboarding={handleConfirmCompletion}
        hasIncompleteTasks={hasIncompleteTasks}
        dialogType="onboarding"
        title="Confirm Completion"
      />
      <OnboardingDialogBox
        isConfirmOpen={isTaskCompletionDialogOpen}
        handleConfirmClose={handleCloseTaskCompletionDialog}
        handleConfirmOnboarding={handleConfirmTaskCompletion}
        isLoading={isCompleting}
        dialogType="taskCompletion"
        title="Confirm Task Completion"
        message="Are you sure you want to complete this task? Once completed, you won't be able to come back and edit the task details again."
        confirmButtonText="Yes, Complete Task"
      />
    </TaskDetailsMainContainer>
  );
};

export default TaskDetails; 